/**
 * v109.9.4 — Edit Access Request UI helpers for the Dispatch bundle.
 *
 * Exports
 * -------
 * • useEditAccessRequest()
 *     - `request({ moduleKey, recordId, recordLabel })` — opens a modal that
 *       collects a reason, POSTs `/edit-access-requests`, and toasts.
 *     - `dialog` — mount once in a page component to render the modal.
 *     - `wrapMutation(fn, opts)` — call any mutation returning a promise; if it
 *       throws HTTP 409 with `edit_window_expired`, we auto-open the request
 *       dialog so the caller doesn't have to handle it.
 *
 * • MasterAdminEditRequestsButton — top-bar bell that opens a review panel
 *   with pending requests. Only rendered for master_admin.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import { toast } from "sonner";
import { KeyRound, ShieldCheck, ShieldX, Bell, Clock, Check, X as IconX, Megaphone, FileText, Award, Plane, ReceiptText } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { DetailsDialog as TravelDetailsDialog } from "../pages/TravelApprovalQueueTab";
import { ViewDialog as TravelBookingDialog } from "../pages/TravelBooking";
import AnnouncementApprovalDialog from "./AnnouncementApprovalDialog";
import DocumentApprovalDialog from "./DocumentApprovalDialog";
import IncentiveApprovalDialog from "./IncentiveApprovalDialog";

const MODULE_LABEL = {
  product_issuance:   "Product Issuance",
  dispatch_orders:    "Dispatch & Logistics",
  vendor_management:  "Vendor Management",
  it_assets:          "IT Assets",
  it_tickets:         "Support Tickets",
  crm_clients:        "Client Database",
  documents:          "Documents",
  announcements:      "Announcements",
  incentive_config:   "Incentive",
  trainers:           "Trainers",
};
const MODULE_WINDOW = {
  product_issuance:   "1 day",
  dispatch_orders:    "1 day",
  vendor_management:  "1 day",
  it_assets:          "—",
  it_tickets:         "—",
  crm_clients:        "—",
  documents:          "—",
  announcements:      "—",
  incentive_config:   "—",
  trainers:           "—",
};

// v109.9.8 — Human-readable field labels (kebab-case → Title Case fallback).
const FIELD_LABEL = {
  vendor_name: "Vendor Name", vendor_gst: "GST Number", vendor_pan: "PAN",
  is_gst_registered: "GST Registered", cin_lip_no: "CIN/LLP", vendor_folder_link: "Folder Link",
  notes: "Notes", change_reason: "Change Reason", delete_reason: "Delete Reason",
  contacts: "Contacts", performance_rating: "Rating", is_pending: "Approval Status",
  dealing_employees: "Dealing With", products_available: "Products", custom_products: "Custom Products",
  status: "Status", tracking_no: "Tracking #", tracking_partner: "Partner",
  no_of_boxes: "Boxes", cost: "Cost", line_items: "Products",
  vendor_id: "Vendor", items: "Items", total_amount: "Total Amount",
  purchase_no: "Purchase #", purchased_at: "Purchased On",
  issuance_no: "Issuance #", employee_id: "Employee", qty_issued: "Qty Issued",
  sku: "SKU", product_name: "Product", quantity: "Quantity", mrp: "MRP",
  purchase_price: "Purchase Price", selling_price: "Selling Price",
  warehouse_location: "Warehouse", categories: "Categories", category: "Category",
  purchased_from: "Purchased From", type: "Type", product_specification: "Specification",
  stock_date: "Stock Date", is_deleted: "Deleted",
  client_name: "Client Name", client_code: "Client Code", assigned_to_id: "Assigned To",
  udise_code: "UDISE", city: "City", state: "State", pin_code: "PIN Code",
  title: "Title", subject: "Subject", description: "Description",
};

// v109.9.8 — Pretty-print any value (dict / list / date / boolean / string).
function _prettyValue(v) {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "number") return v.toLocaleString("en-IN");
  if (typeof v === "string") {
    // parse JSON-ish strings from the backend (server pre-serialises complex fields)
    if (v.startsWith("[") || v.startsWith("{")) {
      try {
        const parsed = JSON.parse(v);
        return _prettyValue(parsed);
      } catch { /* fall through */ }
    }
    // ISO datetime → local date/time
    if (/^\d{4}-\d{2}-\d{2}T/.test(v)) {
      try { return new Date(v).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return v; }
    }
    return v;
  }
  if (Array.isArray(v)) {
    if (v.length === 0) return "(empty)";
    // Arrays of primitives → comma list. Arrays of objects → bullet list of key fields.
    if (v.every((x) => typeof x !== "object" || x === null)) return v.join(", ");
    return v.map((it, i) => {
      const summary = it?.product_name || it?.inventory_name || it?.name
        || it?.sku || it?.tracking_no || `#${i + 1}`;
      const qty = it?.qty ?? it?.qty_issued ?? it?.qty_purchased;
      const price = it?.purchase_price ?? it?.price;
      const bits = [summary];
      if (qty !== undefined) bits.push(`×${qty}`);
      if (price !== undefined && price !== null && price !== "") bits.push(`@ ₹${price}`);
      return bits.join(" ");
    }).join(" · ");
  }
  if (typeof v === "object") {
    const entries = Object.entries(v).filter(([, x]) => x !== "" && x !== null && x !== undefined);
    if (entries.length === 0) return "(empty)";
    return entries.map(([k, x]) => `${FIELD_LABEL[k] || k}: ${_prettyValue(x)}`).join(", ");
  }
  return String(v);
}

function _labelForField(k) {
  return FIELD_LABEL[k] || k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// v109.9.8 — reusable diff renderer used by both bell panel and log tile.
export function ChangesDiff({ changes }) {
  if (!changes || Object.keys(changes).length === 0) return null;
  return (
    <div className="rounded-md border border-slate-200 bg-white px-2 py-2 space-y-1.5" data-testid="approval-changes-diff">
      <div className="text-[9.5px] uppercase tracking-wider font-bold text-slate-500 mb-0.5">
        Proposed Changes
      </div>
      {Object.entries(changes).map(([field, diff]) => (
        <div key={field} className="text-[11.5px]">
          <div className="font-semibold text-slate-700 mb-0.5">{_labelForField(field)}</div>
          <div className="pl-2 border-l-2 border-slate-200 space-y-0.5">
            <div className="flex gap-2">
              <span className="text-[9.5px] uppercase font-bold text-rose-500 shrink-0 mt-0.5">was</span>
              <span className="line-through text-rose-600 break-all">{_prettyValue(diff.from)}</span>
            </div>
            <div className="flex gap-2">
              <span className="text-[9.5px] uppercase font-bold text-emerald-600 shrink-0 mt-0.5">now</span>
              <span className="font-semibold text-emerald-700 break-all">{_prettyValue(diff.to)}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export function useEditAccessRequest() {
  const [state, setState] = useState({ open: false, moduleKey: "", recordId: "", recordLabel: "", proposedPayload: null });
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const request = useCallback(({ moduleKey, recordId, recordLabel = "", proposedPayload = null }) => {
    setReason("");
    setState({ open: true, moduleKey, recordId, recordLabel: recordLabel || recordId, proposedPayload });
  }, []);

  const close = useCallback(() => {
    if (busy) return;
    setState((s) => ({ ...s, open: false }));
    setReason("");
  }, [busy]);

  const submit = useCallback(async () => {
    const r = reason.trim();
    if (r.length < 3) { toast.error("Reason (min 3 chars) required"); return; }
    setBusy(true);
    try {
      const body = {
        module_key: state.moduleKey,
        record_id: state.recordId,
        reason: r,
      };
      if (state.proposedPayload) body.proposed_payload = state.proposedPayload;
      const { data } = await api.post("/edit-access-requests", body);
      if (data?.already_pending) {
        toast("Request already pending. Approver will review shortly.", { icon: "⏳" });
      } else {
        toast.success("Saved & sent for approval — pending approver review");
      }
      setState((s) => ({ ...s, open: false }));
      setReason("");
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  }, [reason, state.moduleKey, state.recordId, state.proposedPayload]);

  // Wrap a mutation. Auto-opens the request dialog when the backend blocks the
  // edit — either via 409 `edit_window_expired` (Dispatch bundle strict window)
  // or (v109.9.7) via 403 when `opts.stageOn403` is true (extended modules with
  // no strict window; the frontend still lets the user "Save & Request").
  // v109.9.5 — Pass `proposedPayload` so on approval the backend auto-applies.
  const wrapMutation = useCallback(async (fn, opts = {}) => {
    try {
      return await fn();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      const is409 = status === 409 && (detail?.code === "edit_window_expired" || detail === "edit_window_expired");
      const is403 = status === 403 && !!opts.stageOn403;
      if (is409 || is403) {
        request({
          moduleKey: (detail && detail.module_key) || opts.moduleKey,
          recordId: (detail && detail.record_id) || opts.recordId,
          recordLabel: opts.recordLabel || "",
          proposedPayload: opts.proposedPayload || null,
        });
        return null;
      }
      throw err;
    }
  }, [request]);

  const dialog = (
    <Dialog open={state.open} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent className="max-w-md" data-testid="edit-req-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound size={16} className="text-amber-600" />
            {state.proposedPayload ? "Save & Send for Approval" : "Request Edit Access"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-[13px]">
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-800">
            The direct-edit window ({MODULE_WINDOW[state.moduleKey] || "—"}) has passed
            for this <b>{MODULE_LABEL[state.moduleKey] || state.moduleKey}</b> record.
            {state.proposedPayload
              ? " Your changes will be staged pending approver review — the record is NOT updated until approved."
              : " Approver will approve or reject."}
          </div>
          {state.recordLabel && (
            <div className="text-[12px] text-slate-500">
              Record: <span className="mono font-semibold text-slate-800">{state.recordLabel}</span>
            </div>
          )}
          <div>
            <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Reason <span className="text-rose-500">*</span></Label>
            <Textarea
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value.slice(0, 500))}
              placeholder="e.g. Vendor name typo / wrong qty on line 2 / need to add missing tracking partner…"
              className="mt-1"
              data-testid="edit-req-reason"
              autoFocus
            />
            <div className="text-[10px] text-slate-400 flex justify-between mt-1">
              <span>{reason.trim().length < 3 ? "Min 3 characters" : "Looks good"}</span>
              <span>{reason.length}/500</span>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={close} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy || reason.trim().length < 3} data-testid="edit-req-submit">
            {busy ? "Sending…" : (state.proposedPayload ? "Save & Send for Approval" : "Send Request")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  return { request, dialog, wrapMutation };
}

/* ============================================================================
 * Universal Approval Requests bell (v109.9.5 — every user; assignee-scoped).
 * Shows requests assigned to the current user (auto-escalate to Master Admin
 * after 24h on the server side). Renders diff with red strikethrough for old
 * values and bold-green new values.
 * ==========================================================================*/
export function MasterAdminEditRequestsButton() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [count, setCount] = useState(0);
  const [travelCount, setTravelCount] = useState(0);
  const [travelRows, setTravelRows] = useState([]);
  const [travelDetails, setTravelDetails] = useState(null); // v109.9.114 — full-details banner
  const [travelStep, setTravelStep] = useState(null); // { row, action } — approve/reject sub-dialog
  const [travelRemark, setTravelRemark] = useState("");
  const [travelBusy, setTravelBusy] = useState(false);
  const [travelUnsettled, setTravelUnsettled] = useState(null);
  const [bookingBanner, setBookingBanner] = useState(null); // v109.9.115 — Travel Booking view banner
  const [bookingAction, setBookingAction] = useState(null); // { booking, action }
  const [bookingRemark, setBookingRemark] = useState("");
  const [bookingBusy, setBookingBusy] = useState(false);

  const submitBookingAction = async () => {
    if (!bookingAction) return;
    if (bookingAction.action === "reject" && !bookingRemark.trim()) {
      toast.error("Rejection reason is mandatory");
      return;
    }
    setBookingBusy(true);
    try {
      const ok = bookingAction.action === "approve";
      await api.post(`/travel-bookings/${bookingAction.booking.id}/${ok ? "approve" : "reject"}`, { remark: bookingRemark.trim() });
      toast.success(ok ? "Approved" : "Rejected");
      setBookingAction(null); setBookingRemark(""); setBookingBanner(null);
      loadCount(); loadTravelRows();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBookingBusy(false); }
  };
  const [open, setOpen] = useState(false);
  const [requests, setRequests] = useState([]);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [loading, setLoading] = useState(false);
  const [reviewing, setReviewing] = useState(null); // {id, action: 'approve'|'reject'}
  const [reviewReason, setReviewReason] = useState("");
  const [busy, setBusy] = useState(false);
  // Native announcement-detail dialog (opened when a user clicks an
  // `announcements` request — shows the module's own detail view with
  // Approve/Reject buttons wired to the /announcements endpoints).
  const [annReview, setAnnReview] = useState(null); // { id, announcementId }
  const [annAccessRole, setAnnAccessRole] = useState(null);
  const [docReview, setDocReview] = useState(null); // { id, documentId }
  const [incReview, setIncReview] = useState(null); // { id, submissionId }
  const pollRef = useRef(null);

  // Universal — every logged-in user has an inbox.
  const hasUser = !!user?.id;

  const loadCount = useCallback(async () => {
    if (!hasUser) return;
    try {
      const { data } = await api.get("/edit-access-requests/pending-count");
      setCount(data?.pending || 0);
    } catch { /* ignore */ }
    // v109.9.112 — Travel Approval Requests share the same bell.
    try {
      const { data: amI } = await api.get("/travel-advances/am-i-approver");
      const isMasterUser = user?.role === "master_admin";
      const uidStr = String(user?.id || "");
      if (isMasterUser) {
        // v109.9.114 — MA count = (rows where MA is approver) + (rows > 24h old).
        const [{ data: adv }, { data: st }] = await Promise.all([
          api.get("/travel-advances/my-approval-queue").catch(() => ({ data: [] })),
          api.get("/travel-settlements?scope=approver").catch(() => ({ data: [] })),
        ]);
        const STALE_MS = 24 * 60 * 60 * 1000;
        const isStale = (v) => {
          const t = new Date(v || 0).getTime();
          return Number.isFinite(t) && t > 0 && (Date.now() - t) >= STALE_MS;
        };
        const matches = (docs, submittedField) => (Array.isArray(docs) ? docs : []).filter((r) => {
          const iAm = (r.approvals || []).some((a) => a.user_id === uidStr && a.status === "pending");
          if (iAm) return true;
          return isStale(r[submittedField] || r.created_at);
        }).length;
        const advCount = matches(adv, "created_at");
        const stOpen = (Array.isArray(st) ? st : []).filter((r) => ["Submitted", "AssignerApproved", "ManagerApproved"].includes(r.status));
        const stCount = matches(stOpen, "submitted_at");
        setTravelCount(advCount + stCount);
      } else if (amI?.is_approver && typeof amI?.pending_count === "number" && amI.pending_count >= 0) {
        setTravelCount(amI.pending_count);
      } else if (amI?.is_approver) {
        // Stable-access users (tab-grant) — count the actual merged queue.
        const [{ data: adv }, { data: st }] = await Promise.all([
          api.get("/travel-advances/my-approval-queue").catch(() => ({ data: [] })),
          api.get("/travel-settlements?scope=approver").catch(() => ({ data: [] })),
        ]);
        const advCount = Array.isArray(adv) ? adv.length : 0;
        const stCount = (Array.isArray(st) ? st : []).filter((r) => ["Submitted", "AssignerApproved", "ManagerApproved"].includes(r.status)).length;
        setTravelCount(advCount + stCount);
      } else {
        setTravelCount(0);
      }
    } catch { /* ignore */ }
  }, [hasUser, user?.id, user?.role]);

  const loadTravelRows = useCallback(async () => {
    if (!hasUser) { setTravelRows([]); return; }
    try {
      const [{ data: adv }, { data: st }, bkRes] = await Promise.all([
        api.get("/travel-advances/my-approval-queue").catch(() => ({ data: [] })),
        api.get("/travel-settlements?scope=approver").catch(() => ({ data: [] })),
        // v109.9.115 — Travel Booking approvals join the same bell. Silently
        // swallow 403s for users without booking-approval access.
        api.get("/travel-bookings?scope=approval").catch(() => ({ data: [] })),
      ]);
      const bk = bkRes?.data || [];
      const uidStr = String(user?.id || "");
      const isMaster = user?.role === "master_admin";
      const STALE_MS = 24 * 60 * 60 * 1000;
      const isStale = (submittedAt) => {
        if (!submittedAt) return false;
        const t = new Date(submittedAt).getTime();
        return Number.isFinite(t) && (Date.now() - t) >= STALE_MS;
      };
      const advances = (Array.isArray(adv) ? adv : []).map((r) => ({
        _kind: "advance",
        _raw: r,
        id: r.id,
        employee: r.employee_name || r.created_by_name || "",
        approvers: (r.approvals || []).filter((a) => a.status === "pending").map((a) => a.name || a.role_tag || "").filter(Boolean),
        submitted_at: r.created_at || r.submitted_at || "",
        amount: Number(r.advance_amount || 0),
        code: "",
        purpose: r.travel_purpose || "",
        _iAmApprover: (r.approvals || []).some((a) => a.user_id === uidStr && a.status === "pending"),
      }));
      const settlements = (Array.isArray(st) ? st : [])
        .filter((r) => ["Submitted", "AssignerApproved", "ManagerApproved"].includes(r.status))
        .map((r) => ({
          _kind: "settlement",
          _raw: r,
          id: r.id,
          employee: r.created_by_name || "",
          approvers: (r.approvals || []).filter((a) => a.status === "pending").map((a) => a.name || (a.stage === "assigned_by" ? "Assigned By" : a.stage === "reporting_manager" ? "Reporting Manager" : a.stage === "accounts" ? "Accounts Team" : "")).filter(Boolean),
          submitted_at: r.submitted_at || r.created_at || "",
          amount: Number(r.claim_amount || 0),
          code: r.settlement_code || "",
          purpose: r.kind === "local" ? `Local · ${r.month || ""} ${r.year || ""}`.trim() : (r.purpose_of_travel || ""),
          _iAmApprover: (r.approvals || []).some((a) => a.user_id === uidStr && a.status === "pending"),
        }));
      const bookings = (Array.isArray(bk) ? bk : [])
        .filter((r) => r.status === "Submitted")
        .map((r) => ({
          _kind: "booking",
          _raw: r,
          id: r.id,
          employee: r.created_by_name || "",
          approvers: Array.isArray(r.approver_names) ? r.approver_names : [],
          submitted_at: r.created_at || "",
          amount: 0,
          code: "",
          purpose: `${r.travel_type || ""} · ${r.mode_of_travel || ""} · ${r.from_city || (r.cities?.[0]) || ""} → ${r.to_city || (r.cities?.slice(-1)?.[0]) || ""}`,
          // Bookings target a specific approver list — MA sees only their own
          // + escalated (>24h) rows (same rule as advances/settlements).
          _iAmApprover: Array.isArray(r.approver_user_ids) && r.approver_user_ids.includes(uidStr),
        }));
      const filtered = [...advances, ...settlements, ...bookings].filter((r) => {
        if (!isMaster) return true;
        return r._iAmApprover || isStale(r.submitted_at);
      });
      const merged = filtered.sort((a, b) => new Date(b.submitted_at || 0) - new Date(a.submitted_at || 0));
      setTravelRows(merged);
      setTravelCount(merged.length);
    } catch { /* ignore */ }
  }, [hasUser, user?.id, user?.role]);

  // v109.9.114 — Approver-facing unsettled advance map (fed to DetailsDialog).
  const loadTravelUnsettled = useCallback(async () => {
    try {
      const { data } = await api.get("/travel-advances/unsettled-summary?scope=all");
      const by = {};
      (Array.isArray(data) ? data : []).forEach((u) => { by[u.employee_id] = u; });
      setTravelUnsettled(by);
    } catch { /* ignore */ }
  }, []);

  // v109.9.114 — Approve / reject a travel row without leaving the bell panel.
  const submitTravelStep = async () => {
    if (!travelStep) return;
    if (travelStep.action === "reject" && !travelRemark.trim()) {
      toast.error("Rejection reason is mandatory");
      return;
    }
    setTravelBusy(true);
    try {
      const base = travelStep.row._kind === "settlement" ? "/travel-settlements" : "/travel-advances";
      await api.post(`${base}/${travelStep.row.id}/step-${travelStep.action}`, { remark: travelRemark.trim() });
      toast.success(travelStep.action === "approve" ? "Step approved" : `${travelStep.row._label || "Request"} rejected`);
      setTravelStep(null); setTravelRemark(""); setTravelDetails(null);
      loadCount(); loadTravelRows();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed");
    } finally { setTravelBusy(false); }
  };

  const loadList = useCallback(async () => {
    if (!hasUser) return;
    setLoading(true);
    try {
      // v109.9.77 — Every user, including Master Admin, only sees requests
      // that are truly meant for THEM. Master Admin no longer sees the
      // firehose of "all inboxes" — only requests where they are the
      // assignee (either directly or via 24h escalation).
      const { data } = await api.get(`/edit-access-requests?status=${statusFilter}&scope=inbox`);
      setRequests(data || []);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally { setLoading(false); }
  }, [hasUser, statusFilter]);

  useEffect(() => {
    if (!hasUser) return;
    loadCount();
    pollRef.current = setInterval(loadCount, 60000); // poll every 60s
    return () => clearInterval(pollRef.current);
  }, [hasUser, loadCount]);

  useEffect(() => { if (open) { loadList(); loadTravelRows(); loadTravelUnsettled(); } }, [open, loadList, loadTravelRows, loadTravelUnsettled]);

  // When the panel opens, fetch this user's announcement access role so we
  // can render Approve/Reject inside the native modal correctly.
  useEffect(() => {
    if (!open || !hasUser) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/announcements/access-role");
        if (!cancelled) setAnnAccessRole(data?.access_role || null);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [open, hasUser]);

  const submitReview = async () => {
    if (!reviewing) return;
    const path = `/edit-access-requests/${reviewing.id}/${reviewing.action}`;
    const trimmed = reviewReason.trim();
    if (reviewing.action === "reject" && trimmed.length < 3) {
      toast.error("Reason (min 3 chars) required to reject"); return;
    }
    setBusy(true);
    try {
      await api.post(path, { review_reason: trimmed });
      toast.success(`Request ${reviewing.action}d`);
      setReviewing(null);
      setReviewReason("");
      await Promise.all([loadCount(), loadList()]);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (!hasUser) return null;

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setOpen(true)}
        className="relative h-9 w-9 p-0 rounded-full bg-white/70 dark:bg-slate-800/70 backdrop-blur border border-slate-200 dark:border-slate-700 hover:bg-amber-50 dark:hover:bg-amber-500/15 hover:border-amber-300 dark:hover:border-amber-500/40 active:scale-95 hover:-translate-y-0.5 hover:shadow-md hover:shadow-amber-200/40 dark:hover:shadow-amber-500/20 transition-all duration-150"
        data-testid="edit-req-bell"
        title="Approval Requests"
        data-keep-color
      >
        <KeyRound size={16} className={(count + travelCount) > 0 ? "text-amber-600 dark:text-amber-300" : "text-slate-600 dark:text-slate-300"} />
        {(count + travelCount) > 0 && (
          <Badge
            className="absolute -top-1 -right-1 h-[18px] min-w-[18px] rounded-full bg-rose-600 text-white text-[10px] font-extrabold px-1 ring-2 ring-white dark:ring-slate-900 shadow-sm flex items-center justify-center animate-pulse"
            data-testid="edit-req-bell-count"
            data-keep-color
          >
            {(count + travelCount) > 9 ? "9+" : (count + travelCount)}
          </Badge>
        )}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="edit-req-panel">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound size={17} className="text-amber-600" />
              Approval Requests
            </DialogTitle>
          </DialogHeader>
          <div className="flex items-center gap-1.5 mb-3 flex-wrap">
            {["pending", "approved", "rejected", "all"].map((s) => (
              <Button
                key={s}
                variant={statusFilter === s ? "default" : "outline"}
                size="sm"
                onClick={() => setStatusFilter(s)}
                className="h-7 text-[11px] capitalize"
                data-testid={`edit-req-filter-${s}`}
              >
                {s}
              </Button>
            ))}
          </div>

          {/* v109.9.112 — Travel Approval Requests section */}
          {travelRows.length > 0 && (
            <div className="mb-4 rounded-md border border-emerald-200 dark:border-emerald-800 bg-emerald-50/40 dark:bg-emerald-950/20 p-2" data-testid="travel-approval-section">
              <div className="flex items-center justify-between mb-1.5">
                <div className="text-[11px] uppercase tracking-wider font-semibold text-emerald-900 dark:text-emerald-200 flex items-center gap-1.5">
                  <ShieldCheck size={12} /> Travel Approval Requests
                </div>
                <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px]">
                  {travelRows.length} pending
                </Badge>
              </div>
              <div className="space-y-1.5">
                {travelRows.map((r) => (
                  <button
                    key={`${r._kind}-${r.id}`}
                    onClick={() => {
                      // v109.9.115 — Route to the correct banner per kind.
                      const raw = r._raw || {};
                      if (r._kind === "booking") {
                        setBookingBanner({ ...raw, __for_approval: true });
                        return;
                      }
                      setTravelDetails({
                        ...raw,
                        _kind: r._kind,
                        _label: r._kind === "settlement" ? "Travel Settlement" : "Travel Advance",
                        _amount: r.amount,
                        _employee: r.employee,
                      });
                    }}
                    className="w-full text-left rounded border border-emerald-200 dark:border-emerald-800 bg-white dark:bg-slate-900 px-2.5 py-1.5 hover:bg-emerald-50/70 dark:hover:bg-emerald-900/30 transition-colors"
                    data-testid={`travel-approval-row-${r._kind}-${r.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          {r._kind === "advance"
                            ? <Plane size={11} className="text-sky-600 shrink-0" />
                            : r._kind === "settlement"
                              ? <ReceiptText size={11} className="text-violet-600 shrink-0" />
                              : <Plane size={11} className="text-blue-600 shrink-0" />}
                          <span className="font-semibold text-[12px] text-slate-900 dark:text-slate-100 truncate">{r.employee || "Unknown"}</span>
                          {r.code && <span className="mono text-[10px] text-indigo-700 dark:text-indigo-400 shrink-0">{r.code}</span>}
                          <Badge className={`text-[9px] ${
                            r._kind === "advance" ? "bg-sky-100 text-sky-800 border-sky-200" :
                            r._kind === "settlement" ? "bg-violet-100 text-violet-800 border-violet-200" :
                            "bg-blue-100 text-blue-800 border-blue-200"
                          }`}>
                            {r._kind === "advance" ? "Advance" : r._kind === "settlement" ? "Settlement" : "Booking"}
                          </Badge>
                        </div>
                        <div className="text-[11px] text-slate-600 dark:text-slate-300 line-clamp-1 mt-0.5">{r.purpose || "—"}</div>
                        <div className="text-[10px] text-slate-500 mt-0.5">
                          Approver: <b>{r.approvers.length ? r.approvers.join(", ") : "—"}</b>
                          <span className="mx-1 text-slate-400">·</span>
                          Submitted: {r.submitted_at ? new Date(r.submitted_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        {r._kind === "booking" ? (
                          <span className="text-[10.5px] text-slate-500 italic">Booking</span>
                        ) : (
                          <div className="mono font-bold text-[12px] text-slate-900 dark:text-slate-100">₹ {Number(r.amount || 0).toLocaleString("en-IN")}</div>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
              <div className="text-[10px] text-slate-500 mt-1.5 text-center">Click any row to review full details, then Approve or Reject.</div>
            </div>
          )}
          {loading ? (
            <div className="text-center py-8 text-slate-400 text-sm">Loading…</div>
          ) : requests.length === 0 ? (
            <div className="text-center py-8 text-slate-400 text-sm">No {statusFilter} requests.</div>
          ) : (
            <div className="space-y-2" data-testid="edit-req-list">
              {requests.map((r) => (
                <div key={r.id} className={`rounded-md border p-3 ${r.escalated ? "border-rose-200 bg-rose-50/40" : "border-slate-200 bg-white"}`} data-testid={`edit-req-item-${r.id}`}>
                  <div className="flex items-start justify-between gap-2 mb-1.5">
                    <div className="min-w-0 flex-1">
                      <div className="text-[12.5px] font-bold text-slate-900">
                        {MODULE_LABEL[r.module_key] || r.module_key}
                        <span className="ml-1.5 mono text-[11px] text-violet-700">{r.record_label}</span>
                      </div>
                      <div className="text-[11px] text-slate-500 mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5">
                        <span>from <b className="text-slate-700">{r.user_name}</b>{r.user_role && <span className="text-slate-400"> · {r.user_role.replace(/_/g, " ")}</span>}</span>
                        {r.assignee_ids && r.assignee_ids.length > 1 ? (
                          <span>→ assigned to <b className="text-slate-700">{(r.assignee_names || []).filter(Boolean).join(", ") || "—"}</b> <span className="text-slate-400">(any 1 can approve)</span></span>
                        ) : r.assignee_name ? (
                          <span>→ assigned to <b className="text-slate-700">{r.assignee_name}</b>{r.assignee_role && <span className="text-slate-400"> · {r.assignee_role.replace(/_/g, " ")}</span>}</span>
                        ) : null}
                        <span className="mono text-slate-400">{r.created_at ? new Date(r.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}</span>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      <Badge className={`text-[10px] ${
                        r.status === "pending" ? "bg-amber-100 text-amber-800" :
                        r.status === "approved" ? "bg-emerald-100 text-emerald-800" :
                        "bg-rose-100 text-rose-800"
                      }`}>{r.status}</Badge>
                      {r.escalated && <Badge className="bg-rose-100 text-rose-800 text-[9px]">escalated</Badge>}
                      {r.applied_at && <Badge className="bg-emerald-50 text-emerald-700 text-[9px] ring-1 ring-emerald-200">applied</Badge>}
                    </div>
                  </div>
                  <div className="text-[11.5px] text-slate-700 bg-slate-50 border rounded px-2 py-1.5 mb-2">
                    <b>Reason:</b> {r.reason}
                  </div>
                  {r.changes && Object.keys(r.changes).length > 0 && (
                    <div className="mb-2" data-testid={`edit-req-diff-${r.id}`}>
                      <ChangesDiff changes={r.changes} />
                    </div>
                  )}
                  {(r.status === "approved" || r.status === "rejected") && r.reviewed_at && (
                    <div className={`text-[10.5px] flex items-center gap-1 mb-1 ${r.status === "approved" ? "text-emerald-700" : "text-rose-700"}`}>
                      <Clock size={11} />
                      <b>{r.status === "approved" ? "Approved" : "Rejected"}</b>
                      &nbsp;by <b>{r.reviewed_by_name || "—"}</b>
                      &nbsp;·&nbsp;{new Date(r.reviewed_at).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                      {r.status === "approved" && r.consumed_at && <span className="ml-1 text-slate-500">· change applied</span>}
                    </div>
                  )}
                  {r.review_reason && (
                    <div className="text-[11px] text-slate-500 italic mb-1">
                      Reviewer: {r.reviewed_by_name} — “{r.review_reason}”
                    </div>
                  )}
                  {r.status === "pending" && (
                    <div className="flex items-center gap-1.5 mt-1">
                      {r.module_key === "announcements" ? (
                        <Button
                          size="sm"
                          onClick={() => setAnnReview({ id: r.id, announcementId: r.record_id })}
                          className="h-7 text-[11px] bg-indigo-600 hover:bg-indigo-700"
                          data-testid={`edit-req-open-ann-${r.id}`}
                        >
                          <Megaphone size={11} className="mr-1" /> Open Announcement · Approve / Reject
                        </Button>
                      ) : r.module_key === "documents" ? (
                        <Button
                          size="sm"
                          onClick={() => setDocReview({ id: r.id, documentId: r.record_id })}
                          className="h-7 text-[11px] bg-indigo-600 hover:bg-indigo-700"
                          data-testid={`edit-req-open-doc-${r.id}`}
                        >
                          <FileText size={11} className="mr-1" /> Open Document · Approve / Review
                        </Button>
                      ) : r.module_key === "incentive_config" ? (
                        <Button
                          size="sm"
                          onClick={() => setIncReview({ id: r.id, submissionId: r.record_id })}
                          className="h-7 text-[11px] bg-amber-600 hover:bg-amber-700"
                          data-testid={`edit-req-open-inc-${r.id}`}
                        >
                          <Award size={11} className="mr-1" /> Open Incentive · Review / Approve / Disburse
                        </Button>
                      ) : (
                        <>
                          <Button size="sm" onClick={() => { setReviewing({ id: r.id, action: "approve" }); setReviewReason(""); }} className="h-7 text-[11px] bg-emerald-600 hover:bg-emerald-700" data-testid={`edit-req-approve-${r.id}`}>
                            <Check size={11} className="mr-1" /> Approve
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => { setReviewing({ id: r.id, action: "reject" }); setReviewReason(""); }} className="h-7 text-[11px] text-rose-700 border-rose-200 hover:bg-rose-50" data-testid={`edit-req-reject-${r.id}`}>
                            <IconX size={11} className="mr-1" /> Reject
                          </Button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Approve / Reject reason dialog */}
      <Dialog open={!!reviewing} onOpenChange={(o) => { if (!o && !busy) { setReviewing(null); setReviewReason(""); } }}>
        <DialogContent className="max-w-md" data-testid="edit-req-review-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {reviewing?.action === "approve" ? (
                <><ShieldCheck size={16} className="text-emerald-600" /> Approve Request</>
              ) : (
                <><ShieldX size={16} className="text-rose-600" /> Reject Request</>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-[13px]">
            <div className="text-[12px] text-slate-600">
              {reviewing?.action === "approve"
                ? "Approving will apply the requested changes to the record and log this approval. Optional note:"
                : "Please state why you're rejecting (visible to the requester):"}
            </div>
            <Textarea
              rows={3}
              value={reviewReason}
              onChange={(e) => setReviewReason(e.target.value.slice(0, 500))}
              placeholder={reviewing?.action === "approve" ? "Optional note…" : "e.g. Please raise a new PO instead / correction should be at source system"}
              data-testid="edit-req-review-reason"
              autoFocus
            />
            <div className="text-[10px] text-slate-400 flex justify-between">
              <span>{reviewing?.action === "reject" && reviewReason.trim().length < 3 ? "Min 3 chars for reject" : ""}</span>
              <span>{reviewReason.length}/500</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setReviewing(null); setReviewReason(""); }} disabled={busy}>Cancel</Button>
            <Button
              onClick={submitReview}
              disabled={busy || (reviewing?.action === "reject" && reviewReason.trim().length < 3)}
              className={reviewing?.action === "approve" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700"}
              data-testid="edit-req-review-submit"
            >
              {busy ? "Saving…" : (reviewing?.action === "approve" ? "Approve" : "Reject")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Native announcement detail — opened for `announcements` requests */}
      <AnnouncementApprovalDialog
        open={!!annReview}
        onClose={() => setAnnReview(null)}
        announcementId={annReview?.announcementId}
        accessRole={annAccessRole}
        onActionDone={async () => {
          await Promise.all([loadCount(), loadList()]);
        }}
      />

      {/* Native document detail — opened for `documents` requests */}
      <DocumentApprovalDialog
        open={!!docReview}
        onClose={() => setDocReview(null)}
        documentId={docReview?.documentId}
        currentUser={user ? { id: user.id, role: user.role } : null}
        onActionDone={async () => {
          await Promise.all([loadCount(), loadList()]);
        }}
      />

      {/* Native incentive detail — opened for `incentive_config` requests */}
      <IncentiveApprovalDialog
        open={!!incReview}
        onClose={() => setIncReview(null)}
        submissionId={incReview?.submissionId}
        onActionDone={async () => {
          await Promise.all([loadCount(), loadList()]);
        }}
      />

      {/* v109.9.114 — Travel details banner + step-confirm dialog, opened
          from the bell panel so the approver can act without leaving the
          top bar. */}
      {travelDetails && (
        <TravelDetailsDialog
          row={travelDetails}
          unsettledMap={travelUnsettled}
          onClose={() => setTravelDetails(null)}
          onApprove={() => { setTravelStep({ row: travelDetails, action: "approve" }); setTravelRemark(""); }}
          onReject={() => { setTravelStep({ row: travelDetails, action: "reject" }); setTravelRemark(""); }}
        />
      )}

      {/* v109.9.115 — Travel Booking banner (reused from TravelBooking.jsx)
          + Approve / Reject confirm dialog with mandatory reason on reject. */}
      {bookingBanner && (
        <TravelBookingDialog
          viewing={bookingBanner}
          setViewing={setBookingBanner}
          access={{ i_can_approve: true, i_can_book: false, i_can_reimburse: false, is_master: user?.role === "master_admin", approval_user_ids: [], ticket_user_ids: [] }}
          onChanged={() => { loadCount(); loadTravelRows(); }}
          onApprove={(b) => { setBookingAction({ booking: b, action: "approve" }); setBookingRemark(""); }}
          onReject={(b) => { setBookingAction({ booking: b, action: "reject" }); setBookingRemark(""); }}
        />
      )}

      <Dialog open={!!bookingAction} onOpenChange={(v) => { if (!v) { setBookingAction(null); setBookingRemark(""); } }}>
        <DialogContent className="max-w-md" data-testid="bell-booking-action-dialog">
          <DialogHeader>
            <DialogTitle className="heading">
              {bookingAction?.action === "approve" ? "Approve travel booking" : "Reject travel booking — reason required"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="text-[12px] text-slate-600 dark:text-slate-300">
              <b>{bookingAction?.booking?.traveler_full_name || bookingAction?.booking?.created_by_name}</b> · {bookingAction?.booking?.travel_type} · {bookingAction?.booking?.mode_of_travel}
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">
                Remark {bookingAction?.action === "reject" ? "*" : "(optional)"}
              </Label>
              <Textarea
                rows={3}
                value={bookingRemark}
                onChange={(e) => setBookingRemark(e.target.value)}
                placeholder={bookingAction?.action === "reject" ? "Why is this being rejected?" : "Optional note"}
                className="mt-1.5"
                data-testid="bell-booking-action-remark"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setBookingAction(null); setBookingRemark(""); }} disabled={bookingBusy}>Cancel</Button>
            <Button
              onClick={submitBookingAction}
              disabled={bookingBusy}
              className={bookingAction?.action === "reject" ? "bg-rose-600 hover:bg-rose-700 text-white" : "bg-emerald-600 hover:bg-emerald-700 text-white"}
              data-testid="bell-booking-action-submit"
            >
              {bookingBusy ? "Working…" : (bookingAction?.action === "approve" ? "Confirm Approval" : "Confirm Rejection")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <Dialog open={!!travelStep} onOpenChange={(v) => { if (!v) { setTravelStep(null); setTravelRemark(""); } }}>
        <DialogContent className="max-w-md" data-testid="bell-travel-step-dialog">
          <DialogHeader>
            <DialogTitle className="heading">
              {travelStep?.action === "approve"
                ? "Approve this step"
                : `Reject ${travelStep?.row?._label || "Request"} — reason required`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="text-[12px] text-slate-600 dark:text-slate-300">
              {travelStep?.row?._label} for <b>{travelStep?.row?._employee}</b> · ₹ {Number(travelStep?.row?._amount || 0).toLocaleString("en-IN")}
              {travelStep?.row?._kind === "settlement" && travelStep?.row?.settlement_code && (
                <span className="ml-2 mono text-indigo-700">· {travelStep.row.settlement_code}</span>
              )}
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">
                Remark {travelStep?.action === "reject" ? "*" : "(optional)"}
              </Label>
              <Textarea
                rows={3}
                value={travelRemark}
                onChange={(e) => setTravelRemark(e.target.value)}
                placeholder={travelStep?.action === "reject" ? "Why is this being rejected?" : "Optional note"}
                className="mt-1.5"
                data-testid="bell-travel-step-remark"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setTravelStep(null); setTravelRemark(""); }} disabled={travelBusy}>Cancel</Button>
            <Button
              onClick={submitTravelStep}
              disabled={travelBusy}
              className={travelStep?.action === "reject" ? "bg-rose-600 hover:bg-rose-700 text-white" : "bg-emerald-600 hover:bg-emerald-700 text-white"}
              data-testid="bell-travel-step-submit"
            >
              {travelBusy ? "Working…" : (travelStep?.action === "approve" ? "Confirm Approval" : "Confirm Rejection")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
